/**
 * `getUserMedia` kısıtları — Rapor 12 §2, Rapor 06 §5.4.
 *
 * ⚠️ **`ideal` kullanılır, `exact` DEĞİL** (cihaz kimliği hariç). `exact` bir
 *    kısıt karşılanamazsa `OverconstrainedError` fırlatır ve kullanıcı
 *    kamerasına hiç erişemez. Ucuz bir dizüstü kamerası 720p veremeyebilir;
 *    doğru davranış "en yakınını ver"dir, "hata ver" değil.
 *
 * ⚠️ Cihaz kimliğinde **`exact` ZORUNLUDUR**: `ideal` verildiğinde tarayıcı
 *    kullanıcının seçtiği mikrofonu sessizce yok sayabilir ve kullanıcı yanlış
 *    cihazdan konuşur.
 */

/**
 * ⚠️ Üç ses işleme bayrağı da **açıktır**. Kapatmak "daha doğal ses" değil,
 *    yankı ve geri besleme demektir: hoparlörden çıkan ses mikrofona geri
 *    girer ve toplantı kullanılamaz hâle gelir.
 */
export const AUDIO_CONSTRAINTS = Object.freeze({
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
});

/**
 * ⭐ **MÜZİK MODU — üç işleme bayrağı da KAPANIR.**
 *
 * Yukarıdaki uyarı ("kapatmak yankı demektir") **konuşma** için geçerlidir ve
 * hâlâ doğrudur. Müzik modu bilinçli bir istisnadır: enstrüman çalan ya da
 * kayıt paylaşan biri için bu üç bayrak sesi *bozar*, düzeltmez —
 * `noiseSuppression` sürekli tonları gürültü sanıp keser, `autoGainControl`
 * dinamiği (piyanissimo/fortissimo farkını) ezer, `echoCancellation` ise
 * hoparlörden gelenle çakışan frekansları müzikten söker.
 *
 * ### 🚨 Bu mod KULAKLIK GEREKTİRİR
 * `echoCancellation: false` ile hoparlör kullanmak, odadaki herkese geri
 * besleme yollamaktır. Bu yüzden arayüzde bir tercih olarak değil, **uyarılı**
 * bir seçenek olarak sunulur ve varsayılan asla bu değildir.
 */
export const MUSIC_AUDIO_CONSTRAINTS = Object.freeze({
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
});

/**
 * Simulcast'in `f` katmanı 720p'dir (`CAMERA_ENCODINGS`). Daha yükseğini
 * istemek yayın CPU'sunu artırır ama SFU zaten ölçekleyip düşürür.
 */
export const VIDEO_CONSTRAINTS = Object.freeze({
  width: Object.freeze({ ideal: 1280 }),
  height: Object.freeze({ ideal: 720 }),
  frameRate: Object.freeze({ ideal: 30, max: 30 }),
});

/**
 * ⭐ **HD yakalama — `CAMERA_ENCODINGS_HD`'nin `f` katmanı 1080p'dir.**
 *
 * ⚠️ Burada da `ideal` kullanılır, `exact` DEĞİL. 1080p veremeyen bir kamerada
 *    `exact` `OverconstrainedError` fırlatır ve kullanıcı HD bir odaya
 *    **hiç kamera açamadan** girer. Doğru davranış "elinden gelenin en iyisini
 *    ver"dir: 720p kamera 720p yayınlar, simulcast tablosu yine çalışır
 *    (bölenler göreli olduğu için karolar orantılı küçülür).
 *
 * ⚠️ Kare hızı 30'da BIRAKILIR. 1080p60 kamera, kodlama yükünü ikiye katlayıp
 *    konuşan kafa görüntüsünde hiçbir şey kazandırmaz; 60 fps'in değerli
 *    olduğu yer ekran paylaşımıdır (`SCREEN_PROFILES.hd`).
 */
export const VIDEO_CONSTRAINTS_HD = Object.freeze({
  width: Object.freeze({ ideal: 1920 }),
  height: Object.freeze({ ideal: 1080 }),
  frameRate: Object.freeze({ ideal: 30, max: 30 }),
});

export function videoConstraintsFor(tier: "standard" | "hd"): MediaTrackConstraints {
  return tier === "hd" ? { ...VIDEO_CONSTRAINTS_HD } : { ...VIDEO_CONSTRAINTS };
}

export interface DevicePreferences {
  audioDeviceId: string | null;
  videoDeviceId: string | null;
  /** Mikrofon **açık mı** başlasın (track yine de alınır: `enabled=false`). */
  mic: boolean;
  /** ⚠️ `false` ise kamera **HİÇ İSTENMEZ** → LED yanmaz. */
  cam: boolean;
}

export const DEFAULT_DEVICE_PREFERENCES: DevicePreferences = Object.freeze({
  audioDeviceId: null,
  videoDeviceId: null,
  mic: true,
  cam: true,
});

/**
 * Tercihleri `getUserMedia` kısıtına çevirir.
 *
 * ⚠️ `cam: false` iken `video: false` döner — `video: true` alıp track'i
 *    kapatmak kamerayı bir an için AÇAR ve LED yanıp söner. Kullanıcı bunu
 *    "kamerasız katıldım ama ışık yandı" olarak görür ve güveni kaybolur.
 */
/**
 * 🚨 **`noiseSuppression` SABİT `true` İDİ — AYAR HİÇBİR ŞEY YAPMIYORDU.**
 *
 * Katılım yolu (`acquireLocal`) her zaman `AUDIO_CONSTRAINTS`'i kullanıyordu.
 * Kullanıcı efekt panelinde "Kapalı" seçse bile tarayıcı gürültü bastırması
 * çalışmaya devam ediyordu; aynı ayar, toplantı içinde değiştirildiğinde
 * (`setEffects`) ise **farklı** sonuç veriyordu. Aynı düğme iki farklı davranış.
 *
 * ⚠️ `echoCancellation` ve `autoGainControl` parametrelenmez: onları kapatmak
 *    "daha doğal ses" değil, hoparlörden mikrofona geri besleme demektir ve
 *    toplantıyı herkes için kullanılamaz hâle getirir.
 */
/**
 * ⚠️ **MÜZİK MODU VE KATMAN BURADAN GEÇMEK ZORUNDA.**
 *
 * `getUserMedia`'yı çıplak `{ audio: { echoCancellation: false } }` ile
 * çağırmak cazip ve **yanlıştır**: kısıt nesnesi eksiksizdir, birleşmez.
 * Cihaz kimliği verilmezse tarayıcı seçili kulaklığı/USB mikrofonu sessizce
 * bırakıp dahili mikrofona döner — menü hâlâ kulaklığı gösterirken. Aynı hata
 * bu dosyada bir kez yapıldı (bkz. yukarıdaki `noiseSuppression` kaydı); müzik
 * modu için tekrarlanmaması adına tek giriş noktası burasıdır.
 *
 * ⚠️ `music` **`noiseSuppression` parametresini EZER**. Kullanıcının efekt
 *    panelindeki gürültü kademesi ile müzik modu çelişirse kazanan müzik
 *    modudur: onu açan kişi bilinçli olarak "sesime dokunma" demiştir.
 */
export function toConstraints(
  prefs: DevicePreferences,
  audio?: { noiseSuppression?: boolean; music?: boolean },
  tier: "standard" | "hd" = "standard",
): MediaStreamConstraints {
  const audioBase = audio?.music
    ? { ...MUSIC_AUDIO_CONSTRAINTS }
    : {
        ...AUDIO_CONSTRAINTS,
        ...(audio?.noiseSuppression === undefined
          ? {}
          : { noiseSuppression: audio.noiseSuppression }),
      };
  const video = videoConstraintsFor(tier);
  return {
    audio: prefs.audioDeviceId
      ? { ...audioBase, deviceId: { exact: prefs.audioDeviceId } }
      : { ...audioBase },
    video: prefs.cam
      ? prefs.videoDeviceId
        ? { ...video, deviceId: { exact: prefs.videoDeviceId } }
        : video
      : false,
  };
}
